import { RefreshCcw, ShieldCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSync } from "../../providers/sync-provider";

interface StatusDockProps {
  licenseValid: boolean;
}

type SyncKind = "idle" | "syncing" | "error" | "offline";

/**
 * Latches a transient `syncing` state for at least `minMs` so the spinner
 * doesn't flicker when a push/pull resolves in a few milliseconds. The
 * animation always gets a full visible cycle before falling back to the
 * real status.
 */
function useLatchedSyncKind(kind: SyncKind, minMs = 700): SyncKind {
  const [shown, setShown] = useState<SyncKind>(kind);
  const startedAt = useRef<number | null>(null);

  useEffect(() => {
    if (kind === "syncing") {
      startedAt.current = Date.now();
      setShown("syncing");
      return;
    }
    if (shown !== "syncing") {
      setShown(kind);
      return;
    }
    const elapsed = startedAt.current ? Date.now() - startedAt.current : minMs;
    const remaining = Math.max(0, minMs - elapsed);
    const t = window.setTimeout(() => setShown(kind), remaining);
    return () => window.clearTimeout(t);
  }, [kind, shown, minMs]);

  return shown;
}

/**
 * Bottom status dock — sync state + license badge. Exit/logout lives in the
 * header (single source of truth) so the dock stays purely informational.
 * Sits over `env(safe-area-inset-bottom)` so it never collides with gesture bars.
 */
export function StatusDock({ licenseValid }: StatusDockProps) {
  const { status, engine, clientId } = useSync();

  const pending = "pending" in status ? status.pending : 0;
  const displayedKind = useLatchedSyncKind(status.kind as SyncKind);

  const syncDot =
    displayedKind === "syncing"
      ? "bg-yellow-400 animate-pulse"
      : displayedKind === "error"
        ? "bg-red-500"
        : displayedKind === "offline"
          ? "bg-gray-400"
          : "bg-pitch-lime";

  const syncLabel =
    displayedKind === "syncing"
      ? "Sync läuft"
      : displayedKind === "error"
        ? "Fehler"
        : displayedKind === "offline"
          ? "Offline"
          : "Synchron";

  const spinning = displayedKind === "syncing";

  return (
    <footer
      className="glass-floating relative z-10 flex items-center gap-2 rounded-3xl px-3 py-2"
      style={{ paddingBottom: `max(8px, env(safe-area-inset-bottom))` }}
    >
      <button
        type="button"
        onClick={() => engine?.syncNow().catch(() => {})}
        title={`client ${clientId?.slice(0, 8) ?? "—"} · ${pending} ausstehend`}
        className="flex flex-1 items-center gap-2 rounded-2xl px-3 py-2 text-left transition-colors"
        style={{
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <RefreshCcw
          className={`size-4 shrink-0 text-muted-foreground transition-transform ${spinning ? "animate-spin" : ""}`}
          aria-hidden
        />
        <span className={`size-2 shrink-0 rounded-full ${syncDot}`} aria-hidden />
        <span
          className="text-[12px] uppercase tracking-[0.16em]"
          style={{ color: "var(--color-on-surface-variant)" }}
        >
          {syncLabel}
        </span>
        {pending > 0 ? (
          <span
            className="ml-auto rounded-full px-1.5 text-[11px] font-bold"
            style={{
              background: "rgba(151,252,0,0.18)",
              color: "var(--color-pitch-lime)",
            }}
          >
            {pending}
          </span>
        ) : null}
      </button>

      <div
        className="flex items-center gap-2 rounded-2xl px-3 py-2"
        style={{
          background: licenseValid ? "rgba(151,252,0,0.10)" : "rgba(255,59,48,0.10)",
          border: licenseValid
            ? "1px solid rgba(151,252,0,0.30)"
            : "1px solid rgba(255,59,48,0.30)",
        }}
      >
        <ShieldCheck
          className="size-4"
          style={{
            color: licenseValid ? "var(--color-pitch-lime)" : "var(--color-tactical-red)",
          }}
          aria-hidden
        />
        <span
          className="text-[12px] uppercase tracking-[0.16em]"
          style={{
            color: licenseValid ? "var(--color-pitch-lime)" : "var(--color-tactical-red)",
            fontWeight: 600,
          }}
        >
          {licenseValid ? "Lizenz aktiv" : "Lizenz prüfen"}
        </span>
      </div>
    </footer>
  );
}
